import type { Dispatch, MutableRefObject } from "react";
import { apiFetchPinnedMessages, buildPinnedMessageContextId, getStoredJwt } from "../../lib/authApi";
import {
  advanceLastSyncedSeq,
  deleteMessageById,
  getContact,
  getMessage,
  listOutboxEntriesForPeer,
  mergeMessagePatch,
  removeOutboxEntry,
  saveMessage,
  syncContactsFromServer,
  upsertContact,
  type ContactRow,
  type IdentityRow,
  type MessageRow,
} from "../../lib/db";
import {
  b64ToU8,
  decryptDirectMessage,
  signChallenge,
  TOMBSTONE_ENVELOPE_PLAINTEXT,
  type IdentityKeys,
  u8ToB64,
} from "../../lib/crypto";
import type { StyxFrame } from "../../lib/frame";
import { newFrameId } from "../../lib/frame";
import type { VoiceCallSession } from "../../lib/webrtcCall";
import {
  syncPushSubscriptionIfGranted,
} from "../../lib/push";
import type { BootItem } from "../types";
import {
  notifyWorkspaceTopicListStale,
  scheduleWorkspaceTopicMarkRead,
} from "../../lib/workspaceTopicListSync";
import { serverWorkspaceMessageToRow, workspaceTopicPeerKey } from "../../lib/workspaceTopicMessages";
import { dmPeerIdFromConversation, type ChatAction, type ConversationRef } from "../state/chatState";
import type { ContactsAction } from "../state/contactsState";
import type { RelayAction } from "../state/relayState";
import type { BootKey } from "../state/domainTypes";
import type { IncomingReq } from "../state/domainTypes";
import { mergeIncoming } from "../state/appUtils";
import type { PresenceStatus } from "../../styles/theme";
import { avatarUrlsFromWire, type AvatarUrls } from "../../lib/avatarDisplay";
import { isPostSwRestartBoot } from "../../lib/postSwReloadSession";

export type SendRealtimeCommand = (type: string, payload: Record<string, unknown>, id?: string) => boolean;

export type FrameRouterDeps = {
  getKeys: () => IdentityKeys | null;
  getIdentityRow: () => IdentityRow | null;
  getSelectedConversation: () => ConversationRef | null;
  getIncoming: () => IncomingReq[];
  isWsLeader: () => boolean;
  voiceSessionRef: MutableRefObject<VoiceCallSession | null>;
  sendRealtimeCommand: SendRealtimeCommand;
  setBoot: (key: BootKey, nextState: BootItem["state"], detail?: string) => void | Promise<void>;
  dispatchChat: Dispatch<ChatAction>;
  dispatchContacts: Dispatch<ContactsAction>;
  dispatchRelay: Dispatch<RelayAction>;
  setMyAvatarUrls: (urls: AvatarUrls | null) => void;
  refreshContacts: () => Promise<void>;
  refreshUnreadCounts: () => Promise<void>;
  notifyContactsChanged: () => void;
  notifyMessagesChanged: (peerId: string) => void;
  repairSync: () => Promise<void>;
  hideBoot: () => void;
};

export async function applyAuthOkPayload(
  payload: Record<string, unknown>,
  deps: Pick<
    FrameRouterDeps,
    | "setMyAvatarUrls"
    | "refreshContacts"
    | "refreshUnreadCounts"
    | "dispatchContacts"
    | "dispatchRelay"
    | "sendRealtimeCommand"
    | "setBoot"
    | "hideBoot"
    | "notifyContactsChanged"
    | "repairSync"
    | "getIncoming"
  >,
): Promise<void> {
  const contactList = (payload.contacts as ContactRow[] | undefined) ?? [];
  const incomingRequests = (payload.incoming_contact_requests as IncomingReq[] | undefined) ?? [];

  {
    const urls = avatarUrlsFromWire(
      payload.avatar as AvatarUrls | undefined,
      typeof payload.avatar_url === "string" ? payload.avatar_url : undefined,
    );
    deps.setMyAvatarUrls(urls);
  }

  await syncContactsFromServer(contactList);

  await deps.refreshContacts();
  await deps.refreshUnreadCounts();

  deps.dispatchContacts({
    type: "set-incoming",
    incoming: mergeIncoming(deps.getIncoming(), incomingRequests),
  });
  deps.dispatchRelay({ type: "set-authenticated", authenticated: true });

  deps.sendRealtimeCommand("presence.update", { status: "online" }, newFrameId());
  void deps.setBoot("auth", "done", "session verified");
  const hideDelayMs = isPostSwRestartBoot() ? 0 : 900;
  window.setTimeout(() => deps.hideBoot(), hideDelayMs);

  deps.notifyContactsChanged();
  await deps.repairSync();
  void syncPushSubscriptionIfGranted();
}

export async function routeFrame(frame: StyxFrame, deps: FrameRouterDeps | null): Promise<void> {
  if (!deps) return;

  const activeKeys = deps.getKeys();
  const activeIdentity = deps.getIdentityRow();
  const seq =
    typeof (frame.payload as { seq?: unknown }).seq === "number"
      ? ((frame.payload as { seq: number }).seq ?? 0)
      : 0;
  if (seq > 0) {
    await advanceLastSyncedSeq(seq);
  }

  const selectedDmPeerId = dmPeerIdFromConversation(deps.getSelectedConversation());

  switch (frame.type) {
    case "auth.challenge": {
      if (!activeKeys || !activeIdentity) return;
      deps.setBoot("auth", "active", "signing challenge");
      const nonceB64 = frame.payload.nonce;
      if (typeof nonceB64 !== "string") return;
      const signature = signChallenge(b64ToU8(nonceB64), activeKeys.secretKeyEd);
      const jwt = getStoredJwt();
      if (!jwt) {
        deps.dispatchChat({
          type: "set-error",
          error: "AUTH: нет токена сессии — войдите снова",
        });
        return;
      }
      const authPayload: Record<string, unknown> = {
        user_id: activeKeys.userId,
        public_key_ed: u8ToB64(activeKeys.publicKeyEd),
        public_key_x: u8ToB64(activeKeys.publicKeyX),
        signature: u8ToB64(signature),
        access_token: jwt,
      };
      deps.sendRealtimeCommand("auth.response", authPayload, newFrameId());
      return;
    }

    case "auth.ok":
      await applyAuthOkPayload(frame.payload as Record<string, unknown>, deps);
      return;

    case "sys.error": {
      const code = frame.payload.code;
      const message = frame.payload.message;
      deps.dispatchChat({
        type: "set-error",
        error: `${typeof code === "string" ? code : "?"}: ${typeof message === "string" ? message : "ошибка"}`,
      });
      return;
    }

    case "contacts.list": {
      const listPayload = frame.payload as {
        contacts?: ContactRow[];
        incoming_contact_requests?: IncomingReq[];
      };
      const contactList = listPayload.contacts ?? [];
      const incomingRequests = listPayload.incoming_contact_requests ?? [];
      await syncContactsFromServer(contactList);
      await deps.refreshContacts();
      deps.dispatchContacts({
        type: "set-incoming",
        incoming: mergeIncoming(deps.getIncoming(), incomingRequests),
      });
      deps.notifyContactsChanged();
      return;
    }

    case "contacts.request_notify": {
      const request = frame.payload as IncomingReq;
      if (request.from_id) {
        deps.dispatchContacts({
          type: "set-incoming",
          incoming: mergeIncoming(deps.getIncoming(), [request]),
        });
      }
      return;
    }

    case "contacts.request_ok":
    case "contacts.accept_ok":
      return;

    case "contacts.accepted": {
      const contact = (frame.payload as { contact?: ContactRow }).contact;
      if (contact?.user_id) {
        await upsertContact(contact);
        deps.dispatchContacts({ type: "remove-incoming", userId: contact.user_id });
        await deps.refreshContacts();
        deps.notifyContactsChanged();
      }
      return;
    }

    case "msg.ack": {
      const messageId = (frame.payload as { message_id?: string }).message_id;
      if (typeof messageId !== "string") return;
      await removeOutboxEntry(messageId).catch(() => {});
      const message = await mergeMessagePatch(messageId, { pending: false, queued: false, failed: false });
      if (!message) return;
      deps.notifyMessagesChanged(message.peerId);
      if (selectedDmPeerId === message.peerId) {
        deps.dispatchChat({
          type: "patch-message",
          messageId,
          patch: { pending: false, queued: false, failed: false },
        });
      }
      return;
    }

    case "msg.delete.relay": {
      const messageId = (frame.payload as { message_id?: string }).message_id;
      if (typeof messageId !== "string") return;
      const existing = await getMessage(messageId);
      if (!existing) return;
      const peerId = existing.peerId;
      await mergeMessagePatch(messageId, {
        plaintext: TOMBSTONE_ENVELOPE_PLAINTEXT,
        deleted: true,
      });
      deps.notifyMessagesChanged(peerId);
      if (selectedDmPeerId === peerId) {
        deps.dispatchChat({
          type: "patch-message",
          messageId,
          patch: { plaintext: TOMBSTONE_ENVELOPE_PLAINTEXT, deleted: true },
        });
      }
      return;
    }

    case "msg.relay": {
      if (!activeKeys) return;
      const relayPayload = frame.payload as {
        id?: string;
        from_id?: string;
        nonce?: string;
        ciphertext?: string;
        created_at?: number;
      };

      if (typeof relayPayload.id !== "string" || typeof relayPayload.from_id !== "string") return;

      const peer = await getContact(relayPayload.from_id);
      if (!peer) return;

      const nonce = typeof relayPayload.nonce === "string" ? b64ToU8(relayPayload.nonce) : null;
      const ciphertext = typeof relayPayload.ciphertext === "string" ? b64ToU8(relayPayload.ciphertext) : null;
      if (!nonce || !ciphertext) return;

      let plaintext = "";
      try {
        plaintext = decryptDirectMessage(ciphertext, nonce, b64ToU8(peer.public_key_x), activeKeys.secretKeyX);
      } catch {
        deps.dispatchChat({
          type: "set-decrypt-error",
          error: "Не удалось расшифровать сообщение (неверные ключи или повреждённые данные).",
        });
        return;
      }

      const peerId = relayPayload.from_id;
      const focused = selectedDmPeerId === peerId;
      const rowMessage: MessageRow = {
        id: relayPayload.id,
        peerId,
        from_id: relayPayload.from_id,
        to_id: activeKeys.userId,
        created_at: typeof relayPayload.created_at === "number" ? relayPayload.created_at : Date.now(),
        plaintext,
        pending: false,
        seenLocal: focused,
      };

      await saveMessage(rowMessage);
      deps.notifyMessagesChanged(peerId);
      deps.sendRealtimeCommand("msg.delivery_ack", { message_id: relayPayload.id }, newFrameId());
      if (focused) {
        deps.sendRealtimeCommand("msg.read", { message_id: relayPayload.id }, newFrameId());
        deps.dispatchChat({ type: "upsert-message", message: rowMessage });
      }

      await deps.refreshUnreadCounts();
      return;
    }

    case "workspace.msg.relay": {
      const wp = frame.payload as {
        workspace_id?: string;
        topic_id?: string;
        id?: string;
        author_id?: string;
        body?: string;
        reply_to?: string | null;
        created_at?: number;
      };
      if (
        typeof wp.workspace_id !== "string" ||
        typeof wp.topic_id !== "string" ||
        typeof wp.id !== "string" ||
        typeof wp.author_id !== "string" ||
        typeof wp.body !== "string"
      ) {
        return;
      }
      const createdAt = typeof wp.created_at === "number" ? wp.created_at : Date.now();
      const replyTo =
        wp.reply_to === undefined || wp.reply_to === null
          ? null
          : typeof wp.reply_to === "string"
            ? wp.reply_to
            : null;
      notifyWorkspaceTopicListStale(wp.workspace_id);
      const sel = deps.getSelectedConversation();
      const viewingTopic =
        sel?.kind === "workspace-topic" &&
        sel.workspaceId === wp.workspace_id &&
        sel.topicId === wp.topic_id;
      if (viewingTopic) {
        scheduleWorkspaceTopicMarkRead(wp.workspace_id, wp.topic_id);
        if (activeKeys?.userId === wp.author_id) {
          const relayPeerKey = workspaceTopicPeerKey(wp.workspace_id, wp.topic_id);
          const outboxForTopic = await listOutboxEntriesForPeer(relayPeerKey);
          if (outboxForTopic.length > 0) {
            return;
          }
        }
        const row = serverWorkspaceMessageToRow(wp.workspace_id, wp.topic_id, {
          id: wp.id,
          author_id: wp.author_id,
          body: wp.body,
          reply_to: replyTo,
          created_at: createdAt,
        });
        deps.dispatchChat({ type: "upsert-message", message: row });
      }
      return;
    }

    case "workspace.msg.ack": {
      const ap = frame.payload as {
        client_id?: string;
        workspace_id?: string;
        topic_id?: string;
        message_id?: string;
        author_id?: string;
        body?: string;
        reply_to?: string | null;
        created_at?: number;
      };
      if (
        typeof ap.workspace_id !== "string" ||
        typeof ap.topic_id !== "string" ||
        typeof ap.message_id !== "string" ||
        typeof ap.author_id !== "string" ||
        typeof ap.body !== "string"
      ) {
        return;
      }
      const createdAtAck = typeof ap.created_at === "number" ? ap.created_at : Date.now();
      const replyToAck =
        ap.reply_to === undefined || ap.reply_to === null
          ? null
          : typeof ap.reply_to === "string"
            ? ap.reply_to
            : null;

      const peerKeyAck = workspaceTopicPeerKey(ap.workspace_id, ap.topic_id);
      const entries = await listOutboxEntriesForPeer(peerKeyAck);
      const clientIdAck = typeof ap.client_id === "string" ? ap.client_id : null;
      let match = clientIdAck ? entries.find((e) => e.id === clientIdAck) : undefined;
      if (!match) {
        match = entries.find((e) => {
          if (e.kind !== "workspace-topic" || !e.wsPayload) return false;
          const wp = e.wsPayload;
          return wp.body === ap.body && (wp.reply_to ?? null) === (replyToAck ?? null);
        });
      }
      if (match) {
        await removeOutboxEntry(match.id);
        await deleteMessageById(match.id);
      }

      const rowAck = serverWorkspaceMessageToRow(ap.workspace_id, ap.topic_id, {
        id: ap.message_id,
        author_id: ap.author_id,
        body: ap.body,
        reply_to: replyToAck,
        created_at: createdAtAck,
      });
      await saveMessage(rowAck);
      notifyWorkspaceTopicListStale(ap.workspace_id);
      deps.notifyMessagesChanged(peerKeyAck);

      const selAck = deps.getSelectedConversation();
      const viewingTopic =
        selAck?.kind === "workspace-topic" &&
        selAck.workspaceId === ap.workspace_id &&
        selAck.topicId === ap.topic_id;
      if (viewingTopic) {
        if (match) {
          deps.dispatchChat({ type: "replace-message-id", oldId: match.id, message: rowAck });
        } else {
          deps.dispatchChat({ type: "upsert-message", message: rowAck });
        }
      }
      return;
    }

    case "workspace.reaction.relay": {
      const wp = frame.payload as {
        workspace_id?: string;
        topic_id?: string;
        message_id?: string;
        user_id?: string;
        emoji?: string;
        action?: string;
      };
      if (
        typeof wp.workspace_id !== "string" ||
        typeof wp.topic_id !== "string" ||
        typeof wp.message_id !== "string" ||
        typeof wp.user_id !== "string" ||
        typeof wp.emoji !== "string" ||
        (wp.action !== "add" && wp.action !== "remove")
      ) {
        return;
      }
      notifyWorkspaceTopicListStale(wp.workspace_id);
      const selR = deps.getSelectedConversation();
      if (
        selR?.kind !== "workspace-topic" ||
        selR.workspaceId !== wp.workspace_id ||
        selR.topicId !== wp.topic_id
      ) {
        return;
      }
      const me = deps.getIdentityRow()?.userId ?? "";
      deps.dispatchChat({
        type: "apply-workspace-reaction",
        messageId: wp.message_id,
        userId: wp.user_id,
        emoji: wp.emoji,
        action: wp.action,
        currentUserId: me,
      });
      return;
    }

    case "msg.delivered": {
      const messageId = (frame.payload as { message_id?: string }).message_id;
      if (typeof messageId !== "string") return;
      const message = await mergeMessagePatch(messageId, { delivered: true, pending: false });
      if (!message) return;
      deps.notifyMessagesChanged(message.peerId);
      if (selectedDmPeerId === message.peerId) {
        deps.dispatchChat({ type: "patch-message", messageId, patch: { delivered: true, pending: false } });
      }
      return;
    }

    case "msg.read": {
      const messageId = (frame.payload as { message_id?: string }).message_id;
      if (typeof messageId !== "string") return;
      const message = await mergeMessagePatch(messageId, { read: true, delivered: true, pending: false });
      if (!message) return;
      deps.notifyMessagesChanged(message.peerId);
      if (selectedDmPeerId === message.peerId) {
        deps.dispatchChat({
          type: "patch-message",
          messageId,
          patch: { read: true, delivered: true, pending: false },
        });
      }
      return;
    }

    case "presence.update": {
      const userId = (frame.payload as { user_id?: string }).user_id;
      const status = (frame.payload as { status?: PresenceStatus }).status;
      if (!userId || !status) return;
      deps.dispatchContacts({ type: "set-presence", userId, status });
      return;
    }

    case "workspace.topicpin.relay": {
      const wp = frame.payload as { workspace_id?: string };
      if (typeof wp.workspace_id !== "string") return;
      notifyWorkspaceTopicListStale(wp.workspace_id);
      return;
    }

    case "workspace.msgpin.relay": {
      const wp = frame.payload as {
        workspace_id?: string;
        topic_id?: string;
        context_id?: string;
      };
      if (typeof wp.workspace_id !== "string" || typeof wp.topic_id !== "string") return;
      const sel = deps.getSelectedConversation();
      const me = deps.getIdentityRow()?.userId ?? "";
      if (!me || sel?.kind !== "workspace-topic") return;
      if (sel.workspaceId !== wp.workspace_id || sel.topicId !== wp.topic_id) return;
      const contextId = `wt:${wp.workspace_id}:${wp.topic_id}`;
      void apiFetchPinnedMessages("workspace_topic", contextId)
        .then((pins) => {
          deps.dispatchChat({ type: "set-pinned-messages", pins });
        })
        .catch(() => {});
      return;
    }

    case "dm.msgpin.relay": {
      const dp = frame.payload as { context_id?: string };
      if (typeof dp.context_id !== "string") return;
      const sel = deps.getSelectedConversation();
      const me = deps.getIdentityRow()?.userId ?? "";
      if (!me || sel?.kind !== "dm") return;
      const { contextId } = buildPinnedMessageContextId(sel, me);
      if (contextId !== dp.context_id) return;
      void apiFetchPinnedMessages("dm", contextId)
        .then((pins) => {
          deps.dispatchChat({ type: "set-pinned-messages", pins });
        })
        .catch(() => {});
      return;
    }

    case "call.relay": {
      if (!deps.isWsLeader()) return;
      const cr = frame.payload as { from_id?: string; call_id?: string; signal?: unknown };
      if (
        typeof cr.from_id !== "string" ||
        typeof cr.call_id !== "string" ||
        cr.signal == null ||
        typeof cr.signal !== "object" ||
        Array.isArray(cr.signal)
      ) {
        return;
      }
      deps.voiceSessionRef.current?.handleRelay(cr.from_id, cr.call_id, cr.signal as Record<string, unknown>);
      return;
    }

    case "call.unavailable": {
      if (!deps.isWsLeader()) return;
      const cu = frame.payload as { call_id?: string; reason?: string };
      if (typeof cu.call_id === "string") {
        deps.voiceSessionRef.current?.handleUnavailable(cu.call_id, cu.reason);
      }
      return;
    }

    case "call.ended": {
      if (!deps.isWsLeader()) return;
      const ce = frame.payload as { from_id?: string; call_id?: string };
      if (typeof ce.from_id === "string" && typeof ce.call_id === "string") {
        deps.voiceSessionRef.current?.handleEnded(ce.from_id, ce.call_id);
      }
      return;
    }

    default:
      return;
  }
}
